import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogResult } from 'src/app/models/dialog-result';
import { EnhancementVariantDetail, IMasterSuite, ISettingsVariant } from 'src/app/models/mastersuites/mastersuite';
import { IProduct } from 'src/app/models/products/product';
import { MasterSuitesService } from 'src/app/services/mastersuites.service';

export interface SelectMasterSuitesStepData {
  masterSuites: IMasterSuite[];
  sourceMasterSuites: IMasterSuite[];
  products: IProduct[];
  initialSourceId?: string;
  initialSourceEnhancementVariantId?: string;
  initialDestinationId?: string;
  initialVariantId?: string;
  initialEnhancementVariantId?: string;
}

export interface WizardEnhancementVariantRef {
  id: string;
  name: string;
}

export interface SelectMasterSuitesStepResult {
  sourceMasterSuiteId: string;
  sourceEnhancementVariant: WizardEnhancementVariantRef;
  destinationMasterSuiteId: string;
  destinationSettingsVariantId: string;
  destinationEnhancementVariant: WizardEnhancementVariantRef;
}

@Component({
  selector: 'app-select-master-suites-step',
  templateUrl: './select-master-suites-step.component.html',
  styleUrls: ['./select-master-suites-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class SelectMasterSuitesStepComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  source: IMasterSuite | null = null;
  sourceEnhancementVariants: EnhancementVariantDetail[] = [];
  sourceEnhancementVariant: EnhancementVariantDetail | null = null;
  destination: IMasterSuite | null = null;
  destinationDetail: IMasterSuite | null = null;
  destinationVariant: ISettingsVariant | null = null;
  destinationEnhancementVariants: EnhancementVariantDetail[] = [];
  destinationEnhancementVariant: EnhancementVariantDetail | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectMasterSuitesStepData,
    private readonly dialogRef: MatDialogRef<SelectMasterSuitesStepComponent>,
    private readonly masterSuitesService: MasterSuitesService,
  ) {}

  ngOnInit(): void {
    const initialSource = this.findSourceMasterSuite(this.data.initialSourceId);
    if (initialSource) {
      this.source = initialSource;
      this.restoreSourceEnhancementVariants(initialSource, this.data.initialSourceEnhancementVariantId);
    }
    const initialDestination = this.findAnyMasterSuite(this.data.initialDestinationId);
    if (initialDestination) {
      this.destination = initialDestination;
      this.restoreDestinationDetail(initialDestination, this.data.initialVariantId);
      this.restoreDestinationEnhancementVariants(initialDestination, this.data.initialEnhancementVariantId);
    }
  }

  get availableDestinations(): IMasterSuite[] {
    return this.data.masterSuites.filter((masterSuite) => masterSuite.id !== this.source?.id);
  }

  get availableSources(): IMasterSuite[] {
    return this.data.sourceMasterSuites;
  }

  get destinationVariants(): ISettingsVariant[] {
    return this.destinationDetail?.settingsVariants ?? [];
  }

  setSource(masterSuite: IMasterSuite): void {
    this.source = masterSuite;
    this.sourceEnhancementVariants = [];
    this.sourceEnhancementVariant = null;
    if (this.destination?.id === masterSuite.id) {
      this.destination = null;
      this.destinationDetail = null;
      this.destinationVariant = null;
      this.destinationEnhancementVariants = [];
      this.destinationEnhancementVariant = null;
    }
    this.loadSourceEnhancementVariants(masterSuite);
  }

  setSourceEnhancementVariant(variant: EnhancementVariantDetail): void {
    this.sourceEnhancementVariant = variant;
  }

  setDestination(masterSuite: IMasterSuite): void {
    this.destination = masterSuite;
    this.destinationDetail = null;
    this.destinationVariant = null;
    this.destinationEnhancementVariants = [];
    this.destinationEnhancementVariant = null;
    this.masterSuitesService
      .detailMasterSuite(masterSuite.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((detail) => {
        this.destinationDetail = detail;
        this.destinationVariant = detail.settingsVariants?.[0] ?? null;
      });
    this.loadDestinationEnhancementVariants(masterSuite);
  }

  setDestinationVariant(variant: ISettingsVariant): void {
    this.destinationVariant = variant;
  }

  setDestinationEnhancementVariant(variant: EnhancementVariantDetail): void {
    this.destinationEnhancementVariant = variant;
  }

  canConfirm(): boolean {
    return !!this.source?.id
      && !!this.sourceEnhancementVariant?.id
      && !!this.destination?.id
      && !!this.destinationVariant?.id
      && !!this.destinationEnhancementVariant?.id
      && this.source.id !== this.destination.id;
  }

  confirm(): void {
    if (!this.canConfirm()) return;
    this.dialogRef.close({
      sourceMasterSuiteId: this.source!.id!,
      sourceEnhancementVariant: {
        id: this.sourceEnhancementVariant!.id!,
        name: this.sourceEnhancementVariant!.name,
      },
      destinationMasterSuiteId: this.destination!.id!,
      destinationSettingsVariantId: this.destinationVariant!.id!,
      destinationEnhancementVariant: {
        id: this.destinationEnhancementVariant!.id!,
        name: this.destinationEnhancementVariant!.name,
      },
    } as SelectMasterSuitesStepResult);
  }

  cancel(): void {
    this.dialogRef.close(DialogResult.Cancel);
  }

  private findSourceMasterSuite(id: string | undefined): IMasterSuite | undefined {
    if (!id) return undefined;
    return this.data.sourceMasterSuites.find((masterSuite) => masterSuite.id === id);
  }

  private findAnyMasterSuite(id: string | undefined): IMasterSuite | undefined {
    if (!id) return undefined;
    return this.data.masterSuites.find((masterSuite) => masterSuite.id === id);
  }

  private restoreDestinationDetail(destination: IMasterSuite, preferredVariantId: string | undefined): void {
    this.masterSuitesService
      .detailMasterSuite(destination.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((detail) => {
        this.destinationDetail = detail;
        this.destinationVariant = detail.settingsVariants?.find((variant) => variant.id === preferredVariantId)
          ?? detail.settingsVariants?.[0]
          ?? null;
      });
  }

  private restoreDestinationEnhancementVariants(destination: IMasterSuite, preferredEnhancementVariantId: string | undefined): void {
    this.masterSuitesService
      .getAllEnhancementVariantOfMasterSuite(destination.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.destinationEnhancementVariants = result.enhancementVariants ?? [];
        this.destinationEnhancementVariant = this.destinationEnhancementVariants.find((variant) => variant.id === preferredEnhancementVariantId)
          ?? this.pickDefaultDestinationEnhancementVariant()
          ?? null;
      });
  }

  private loadDestinationEnhancementVariants(destination: IMasterSuite): void {
    this.masterSuitesService
      .getAllEnhancementVariantOfMasterSuite(destination.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.destinationEnhancementVariants = result.enhancementVariants ?? [];
        this.destinationEnhancementVariant = this.pickDefaultDestinationEnhancementVariant() ?? null;
      });
  }

  private pickDefaultDestinationEnhancementVariant(): EnhancementVariantDetail | undefined {
    return this.destinationEnhancementVariants.find((variant) => variant.default)
      ?? this.destinationEnhancementVariants[0];
  }

  private restoreSourceEnhancementVariants(source: IMasterSuite, preferredEnhancementVariantId: string | undefined): void {
    this.masterSuitesService
      .getAllEnhancementVariantOfMasterSuite(source.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.sourceEnhancementVariants = this.filterEnhancementVariantsUsedInBatch(source, result.enhancementVariants ?? []);
        this.sourceEnhancementVariant = this.sourceEnhancementVariants.find((variant) => variant.id === preferredEnhancementVariantId)
          ?? this.pickDefaultSourceEnhancementVariant()
          ?? null;
      });
  }

  private loadSourceEnhancementVariants(source: IMasterSuite): void {
    this.masterSuitesService
      .getAllEnhancementVariantOfMasterSuite(source.id!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.sourceEnhancementVariants = this.filterEnhancementVariantsUsedInBatch(source, result.enhancementVariants ?? []);
        this.sourceEnhancementVariant = this.pickDefaultSourceEnhancementVariant() ?? null;
      });
  }

  private filterEnhancementVariantsUsedInBatch(source: IMasterSuite, variants: EnhancementVariantDetail[]): EnhancementVariantDetail[] {
    const usedIds = new Set(
      this.data.products
        .filter((product) => product.masterSuiteId === source.id)
        .map((product) => product.masterSuiteEnhancementVariantId)
        .filter((id): id is string => !!id),
    );
    return variants.filter((variant) => usedIds.has(variant.id));
  }

  private pickDefaultSourceEnhancementVariant(): EnhancementVariantDetail | undefined {
    return this.sourceEnhancementVariants.find((variant) => variant.default)
      ?? this.sourceEnhancementVariants[0];
  }
}
